import { Button } from '@/components/ui/button'
import { SkeletonRows } from '@/components/ui/skeleton'
import type { EmailThreadPage } from '@/models/internalEmail'
import { EmailThreadListItem } from './EmailThreadListItem'

interface Props {
  page: EmailThreadPage | undefined
  isLoading: boolean
  pageNumber: number
  selectedKey: string | null
  onSelect: (threadKey: string) => void
  onPage: (page: number) => void
  /** Off in an org-scoped list, where it is the same name on every row. */
  showOrg?: boolean
}

export function EmailThreadList({
  page,
  isLoading,
  pageNumber,
  selectedKey,
  onSelect,
  onPage,
  showOrg = true,
}: Props) {
  const threads = page?.results ?? []

  return (
    <div className="bg-card flex h-full flex-col overflow-hidden rounded-lg border">
      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <SkeletonRows className="p-3" rows={8} rowClassName="h-14" />
        ) : threads.length === 0 ? (
          <p className="text-muted-foreground p-6 text-center text-sm">No emails match.</p>
        ) : (
          <ul className="divide-y">
            {threads.map((thread) => (
              <EmailThreadListItem
                key={thread.threadKey}
                thread={thread}
                isSelected={thread.threadKey === selectedKey}
                onSelect={() => onSelect(thread.threadKey)}
                showOrg={showOrg}
              />
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 border-t px-3 py-2">
        <Button variant="outline" size="sm" disabled={!page?.previous} onClick={() => onPage(pageNumber - 1)}>
          Previous
        </Button>
        <span className="text-muted-foreground text-xs">
          Page {pageNumber} · {page?.count ?? 0} threads
        </span>
        <Button variant="outline" size="sm" disabled={!page?.next} onClick={() => onPage(pageNumber + 1)}>
          Next
        </Button>
      </div>
    </div>
  )
}
